import { Navigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";

import AuditLog from "components/views/AuditLog";
import { useAppTabs } from "hooks/useAppTabs";
import usePageTitle from "hooks/usePageTitle";
import NotFoundItem from "routes/404/NotFoundItem";
import AttendancesTable from "routes/swoogo/AttendancesTable";
import EventsTable from "routes/swoogo/EventsTable";
import RegistrantsTable from "routes/swoogo/RegistrantsTable";
import SessionsTable from "routes/swoogo/SessionsTable";
import { useReadableSwoogoSections } from "routes/swoogo/swoogoSections";
import useSwoogoConnected from "routes/swoogo/useSwoogoConnected";

const DEFAULT_ACCOUNT_NAME = "Swoogo";

// The account page: the mirrored Swoogo tables and the account's audit trail.
// The sections are navigated from the Swoogo group in the main sidebar. Folder
// selection and credentials stay in Settings. Mounted at `swoogo/*` behind the
// Swoogo feature flag; the tables only exist once a Swoogo account is
// connected. No default tab: a bare URL redirects to the first readable table.
const SwoogoAccountPage = () => {
  const { activeTab: tab } = useAppTabs({ basePath: "/swoogo" });
  const sections = useReadableSwoogoSections();
  const { connectionId, isConnected, isLoading, accountName } =
    useSwoogoConnected();

  usePageTitle(accountName || DEFAULT_ACCOUNT_NAME);

  if (isLoading) {
    return null;
  }

  if (!isConnected || sections.length === 0) {
    return <NotFoundItem />;
  }

  const activeSection = sections.find(section => section.key === tab);
  if (!activeSection) {
    return <Navigate replace to={`/swoogo/${sections[0].key}`} />;
  }

  const content = {
    events: <EventsTable />,
    registrants: <RegistrantsTable />,
    sessions: <SessionsTable />,
    attendances: <AttendancesTable />,
    activity: (
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
        <div className="tw:flex tw:items-center tw:gap-2.5">
          <span className="tw:inline-flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-primary-foreground">
            <ClipboardList className="tw:size-4" />
          </span>
          <span className="tw:text-sm tw:font-bold">Swoogo Activity Logs</span>
        </div>
        <AuditLog
          modelType="swoogo_connection"
          modelId={connectionId}
          allowManualActivity={false}
        />
      </div>
    ),
  };

  return content[activeSection.key];
};

export default SwoogoAccountPage;
